import { Cookies } from '@confluence/cookies';
import {
	confluenceLocalStorageInstance as localStorage,
	keys as localStorageKeys,
} from '@confluence/storage-manager';

import type { GetWhiteboardParamsResult } from './whiteboard-params';

export const BIFROST_OVERRIDE_COOKIE = 'bifrost-override';

export const getVersionFromCookie = (): string | null => {
	const cookieValue = Cookies.get(BIFROST_OVERRIDE_COOKIE);

	if (!cookieValue) {
		return null;
	}

	try {
		// Decode the URL-encoded JSON string
		const decodedValue = decodeURIComponent(cookieValue);
		const parsedValue = JSON.parse(decodedValue);
		return parsedValue.version || null;
	} catch (err) {
		return null;
	}
};

export const getWhiteboardVersion = (
	params: Pick<GetWhiteboardParamsResult, 'canvas-frontend'>,
) => {
	const version = params['canvas-frontend'];

	// Don't try to read/write local storage in SSR
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		return version;
	}

	return version ?? getVersionFromCookie();
};

export const clearWhiteboardVersion = async () =>
	new Promise<void>((resolve) =>
		/**
		 * "Why on earth is there a setTimeout here?"
		 * Empirically, without this we've had situations where you'd need to click "Exit branch deploy"
		 * twice, because the first time the local storage would not be recognised as cleared.
		 *
		 * This is addressed by adding a 0ms timeout, which moves the inside function to execute and then
		 * resolves a Promise when it's complete. We await this promise in the parent function,
		 * which forces the clearing of the whiteboard version to complete before refreshing.
		 *
		 * Sources:
		 * https://stackoverflow.com/questions/20231163/is-html5-localstorage-asynchronous
		 * https://stackoverflow.com/questions/779379/why-is-settimeoutfn-0-sometimes-useful
		 * https://developer.mozilla.org/en-US/docs/Web/API/setTimeout#delay
		 */
		setTimeout(() => {
			// Don't try to read/write local storage in SSR
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			if (process.env.REACT_SSR) {
				return resolve();
			}

			Cookies.remove(BIFROST_OVERRIDE_COOKIE);
			localStorage.removeItem(localStorageKeys.CANVAS_FRONTEND_VERSION);
			localStorage.removeItem(localStorageKeys.CANVAS_SERVER);
			localStorage.removeItem(localStorageKeys.CANVAS_RELAY);

			return resolve();
		}),
	);
